import { useState, useEffect } from 'react';
import { useTheme, type VisualStyle } from '@/lib/theme-manager';

interface StyleOption {
  id: VisualStyle;
  name: string;
  description: string;
  tooltip: {
    whatItIs: string;
    whenToUse: string;
    whenNotToUse: string;
  };
}

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'system-ui', label: 'System UI' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'JetBrains Mono', label: 'JetBrains Mono' },
];

const STYLE_OPTIONS: StyleOption[] = [
  {
    id: 'glassmorphic',
    name: 'Glassmorphic',
    description: 'Frosted glass with blur and transparency',
    tooltip: {
      whatItIs:
        'A frosted-glass effect. Elements look like translucent panels floating over the background, with soft blur and subtle borders.',
      whenToUse:
        'When you want a modern, premium, and airy feel. Great for dashboards and apps with layered content.',
      whenNotToUse:
        'If your users are on older devices or browsers. The blur effects can be slow on low-powered hardware.',
    },
  },
  {
    id: 'neumorphic',
    name: 'Neumorphic',
    description: 'Soft shadows, raised clay-like surfaces',
    tooltip: {
      whatItIs:
        'A soft, sculpted look where elements appear gently pushed out from the background, like clay or rubber. Uses light and dark shadows instead of borders.',
      whenToUse:
        'When you want a calm, tactile, minimalist interface. Works well for simple layouts with few layers.',
      whenNotToUse:
        'If your app has many nested cards or dense layouts. The shadow effect loses clarity when elements are close together.',
    },
  },
  {
    id: 'neubrutalism',
    name: 'Neubrutalism',
    description: 'Bold borders, hard shadows, high contrast',
    tooltip: {
      whatItIs:
        'A bold, graphic style with thick outlines, hard-offset shadows, and solid colors. Inspired by poster design and print media.',
      whenToUse:
        'When you want a playful, confident, eye-catching look. Great for creative brands that want to stand out.',
      whenNotToUse:
        'If you want a subtle or corporate feel. This style is intentionally loud and may feel aggressive in professional settings.',
    },
  },
];

function getPreviewPanelStyle(style: VisualStyle): React.CSSProperties {
  switch (style) {
    case 'glassmorphic':
      return {
        backgroundColor: 'rgba(30, 30, 36, 0.6)',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(58, 58, 68, 0.5)',
      };
    case 'neumorphic':
      return {
        backgroundColor: '#1E1E24',
        border: '1px solid rgba(58, 58, 68, 0.3)',
        boxShadow:
          '6px 6px 12px rgba(0, 0, 0, 0.5), -6px -6px 12px rgba(58, 58, 68, 0.15)',
      };
    case 'neubrutalism':
      return {
        backgroundColor: '#1E1E24',
        border: '2px solid #E0E0E0',
        boxShadow: '4px 4px 0px #E0E0E0',
      };
  }
}

function StylePreviewSwatch({ style }: { style: VisualStyle }) {
  const swatchStyles: Record<VisualStyle, string> = {
    glassmorphic: 'bg-charcoal-900/60 backdrop-blur-xl border border-charcoal-700/50',
    neumorphic: 'bg-charcoal-900 border border-charcoal-700/30 shadow-neu-raised',
    neubrutalism: 'bg-charcoal-900 border-2 border-slate-200 shadow-brutal',
  };

  return (
    <div className="flex gap-2">
      <div className={`h-8 w-full rounded-lg ${swatchStyles[style]}`} />
      <div className={`h-8 w-2/3 rounded-lg ${swatchStyles[style]}`} />
    </div>
  );
}

export function ThemeSettings() {
  const { theme, updateTheme, resetTheme } = useTheme();
  const [localTheme, setLocalTheme] = useState(theme);
  const [saving, setSaving] = useState(false);

  // Sync local state when theme loads from API
  useEffect(() => {
    setLocalTheme(theme);
  }, [theme]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleColorChange = (key: 'primaryColor' | 'secondaryColor' | 'accentColor' | 'backgroundColor' | 'surfaceColor' | 'textColor', value: string) => {
    setLocalTheme((prev) => ({ ...prev, [key]: value }));
  };

  const handleStyleChange = (style: VisualStyle) => {
    setLocalTheme((prev) => ({ ...prev, visualStyle: style }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      await updateTheme(localTheme);

      setMessage({ type: 'success', text: 'Theme saved successfully!' });
    } catch (err) {
      console.error('Error saving theme:', err);
      setMessage({ type: 'error', text: 'Failed to save theme' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset theme to default colors and style?')) return;

    try {
      setSaving(true);
      setMessage(null);

      await resetTheme();
      setLocalTheme(theme);

      setMessage({ type: 'success', text: 'Theme reset to defaults!' });
    } catch (err) {
      console.error('Error resetting theme:', err);
      setMessage({ type: 'error', text: 'Failed to reset theme' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">Theme Customization</h2>
        <p className="text-slate-400 text-sm">Customize the application's color scheme and appearance</p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Color Pickers */}
      <div className="glass-strong p-6 rounded-xl space-y-6">
        <h3 className="text-lg font-semibold text-white mb-4">Brand Colors</h3>

        {/* Primary Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">Primary Color</label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.primaryColor}
              onChange={(e) => handleColorChange('primaryColor', e.target.value)}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.primaryColor}
                onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg shadow-glow"
              style={{ backgroundColor: localTheme.primaryColor }}
            />
          </div>
        </div>

        {/* Secondary Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">Secondary Color</label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.secondaryColor}
              onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.secondaryColor}
                onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg shadow-glow"
              style={{ backgroundColor: localTheme.secondaryColor }}
            />
          </div>
        </div>

        {/* Accent Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">Accent Color</label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.accentColor}
              onChange={(e) => handleColorChange('accentColor', e.target.value)}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.accentColor}
                onChange={(e) => handleColorChange('accentColor', e.target.value)}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg shadow-glow"
              style={{ backgroundColor: localTheme.accentColor }}
            />
          </div>
        </div>
      </div>

      {/* Background & Text Colors */}
      <div className="glass-strong p-6 rounded-xl space-y-6">
        <h3 className="text-lg font-semibold text-white mb-4">Background & Text</h3>

        {/* Background Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">Background Color</label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.backgroundColor}
              onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.backgroundColor}
                onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg border border-charcoal-600"
              style={{ backgroundColor: localTheme.backgroundColor }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">Main page background color</p>
        </div>

        {/* Surface Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">Surface Color</label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.surfaceColor}
              onChange={(e) => handleColorChange('surfaceColor', e.target.value)}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.surfaceColor}
                onChange={(e) => handleColorChange('surfaceColor', e.target.value)}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg border border-charcoal-600"
              style={{ backgroundColor: localTheme.surfaceColor }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">Cards, panels, and sidebar background</p>
        </div>

        {/* Text Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">Text Color</label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.textColor}
              onChange={(e) => handleColorChange('textColor', e.target.value)}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.textColor}
                onChange={(e) => handleColorChange('textColor', e.target.value)}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg border border-charcoal-600 flex items-center justify-center"
              style={{ backgroundColor: localTheme.backgroundColor }}
            >
              <span style={{ color: localTheme.textColor }} className="text-sm font-medium">Aa</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1">Primary text color across the application</p>
        </div>
      </div>

      {/* Font Family */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Font Family</h3>
        <p className="text-sm text-slate-400 mb-4">Choose the primary font for the application</p>

        <select
          value={localTheme.fontFamily}
          onChange={(e) => setLocalTheme((prev) => ({ ...prev, fontFamily: e.target.value }))}
          className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                   focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
        >
          {FONT_OPTIONS.map((font) => (
            <option key={font.value} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>

        <div
          className="mt-4 p-4 rounded-lg border border-charcoal-600"
          style={{
            fontFamily: `${localTheme.fontFamily}, system-ui, sans-serif`,
            backgroundColor: localTheme.surfaceColor,
            color: localTheme.textColor,
          }}
        >
          <p className="text-lg font-bold mb-1">The quick brown fox jumps over the lazy dog</p>
          <p className="text-sm opacity-70">0123456789 - ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
        </div>
      </div>

      {/* Gradient Settings */}
      <div className="glass-strong p-6 rounded-xl space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">Gradient Controls</h3>
          <p className="text-sm text-slate-400">
            Customize gradient colors independently from brand colors. Leave empty to use brand colors.
          </p>
        </div>

        {/* Gradient Toggle */}
        <div className="flex items-center justify-between p-4 bg-charcoal-800 rounded-lg">
          <div>
            <label className="text-sm font-medium text-slate-300">Enable Gradients</label>
            <p className="text-xs text-slate-500 mt-1">Show gradient backgrounds across the app</p>
          </div>
          <button
            type="button"
            onClick={() => setLocalTheme((prev) => ({ ...prev, gradientEnabled: !prev.gradientEnabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localTheme.gradientEnabled ? 'bg-amber-500' : 'bg-charcoal-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                localTheme.gradientEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Button Glow Toggle */}
        <div className="flex items-center justify-between p-4 bg-charcoal-800 rounded-lg">
          <div>
            <label className="text-sm font-medium text-slate-300">Enable Button Glow</label>
            <p className="text-xs text-slate-500 mt-1">Add subtle glow effect to primary buttons</p>
          </div>
          <button
            type="button"
            onClick={() => setLocalTheme((prev) => ({ ...prev, buttonGlowEnabled: !prev.buttonGlowEnabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localTheme.buttonGlowEnabled ? 'bg-amber-500' : 'bg-charcoal-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                localTheme.buttonGlowEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Gradient Start Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">
            Gradient Start Color
            <span className="text-slate-500 ml-2 font-normal">(defaults to Primary Color)</span>
          </label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.gradientStartColor || localTheme.primaryColor}
              onChange={(e) => setLocalTheme((prev) => ({ ...prev, gradientStartColor: e.target.value }))}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.gradientStartColor || ''}
                onChange={(e) => setLocalTheme((prev) => ({ ...prev, gradientStartColor: e.target.value || null }))}
                placeholder="Use primary color"
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                         placeholder:text-slate-600"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg shadow-glow"
              style={{ backgroundColor: localTheme.gradientStartColor || localTheme.primaryColor }}
            />
            {localTheme.gradientStartColor && (
              <button
                type="button"
                onClick={() => setLocalTheme((prev) => ({ ...prev, gradientStartColor: null }))}
                className="px-3 py-2 bg-charcoal-700 hover:bg-charcoal-600 text-white text-sm rounded-lg"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Gradient Middle Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">
            Gradient Middle Color
            <span className="text-slate-500 ml-2 font-normal">(defaults to Secondary Color)</span>
          </label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.gradientMiddleColor || localTheme.secondaryColor}
              onChange={(e) => setLocalTheme((prev) => ({ ...prev, gradientMiddleColor: e.target.value }))}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.gradientMiddleColor || ''}
                onChange={(e) => setLocalTheme((prev) => ({ ...prev, gradientMiddleColor: e.target.value || null }))}
                placeholder="Use secondary color"
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                         placeholder:text-slate-600"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg shadow-glow"
              style={{ backgroundColor: localTheme.gradientMiddleColor || localTheme.secondaryColor }}
            />
            {localTheme.gradientMiddleColor && (
              <button
                type="button"
                onClick={() => setLocalTheme((prev) => ({ ...prev, gradientMiddleColor: null }))}
                className="px-3 py-2 bg-charcoal-700 hover:bg-charcoal-600 text-white text-sm rounded-lg"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Gradient End Color */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">
            Gradient End Color
            <span className="text-slate-500 ml-2 font-normal">(defaults to Accent Color)</span>
          </label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={localTheme.gradientEndColor || localTheme.accentColor}
              onChange={(e) => setLocalTheme((prev) => ({ ...prev, gradientEndColor: e.target.value }))}
              className="w-16 h-16 rounded-lg cursor-pointer bg-transparent"
            />
            <div className="flex-1">
              <input
                type="text"
                value={localTheme.gradientEndColor || ''}
                onChange={(e) => setLocalTheme((prev) => ({ ...prev, gradientEndColor: e.target.value || null }))}
                placeholder="Use accent color"
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                         placeholder:text-slate-600"
              />
            </div>
            <div
              className="w-24 h-16 rounded-lg shadow-glow"
              style={{ backgroundColor: localTheme.gradientEndColor || localTheme.accentColor }}
            />
            {localTheme.gradientEndColor && (
              <button
                type="button"
                onClick={() => setLocalTheme((prev) => ({ ...prev, gradientEndColor: null }))}
                className="px-3 py-2 bg-charcoal-700 hover:bg-charcoal-600 text-white text-sm rounded-lg"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Gradient Preview */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">Gradient Preview</label>
          <div
            className="h-24 rounded-xl"
            style={{
              background: `linear-gradient(to right, ${localTheme.gradientStartColor || localTheme.primaryColor}, ${localTheme.gradientMiddleColor || localTheme.secondaryColor}, ${localTheme.gradientEndColor || localTheme.accentColor})`,
            }}
          />
        </div>
      </div>

      {/* Visual Style Selector */}
      <div className="glass-strong p-6 rounded-xl space-y-4">
        <h3 className="text-lg font-semibold text-white mb-4">Visual Style</h3>
        <p className="text-sm text-slate-400 mb-4">
          Choose how cards and panels look across the entire application
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STYLE_OPTIONS.map((option) => (
            <div key={option.id} className="relative group">
              <button
                type="button"
                onClick={() => handleStyleChange(option.id)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  localTheme.visualStyle === option.id
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-charcoal-600 bg-charcoal-800 hover:border-charcoal-500'
                }`}
              >
                <div className="mb-3">
                  <StylePreviewSwatch style={option.id} />
                </div>

                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-medium">{option.name}</span>
                  {localTheme.visualStyle === option.id && (
                    <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <p className="text-xs text-slate-400">{option.description}</p>
              </button>

              {/* Tooltip (hover) */}
              <div
                className="absolute left-0 right-0 top-full mt-2 z-50
                           opacity-0 invisible group-hover:opacity-100 group-hover:visible
                           transition-all duration-200 pointer-events-none"
              >
                <div className="bg-charcoal-950 border border-charcoal-600 rounded-lg p-4 shadow-xl">
                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="text-amber-400 font-semibold">What it is:</span>
                      <p className="text-slate-300 mt-0.5">{option.tooltip.whatItIs}</p>
                    </div>
                    <div>
                      <span className="text-emerald-400 font-semibold">Best for:</span>
                      <p className="text-slate-300 mt-0.5">{option.tooltip.whenToUse}</p>
                    </div>
                    <div>
                      <span className="text-amber-400 font-semibold">Avoid if:</span>
                      <p className="text-slate-300 mt-0.5">{option.tooltip.whenNotToUse}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Live Preview */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Preview</h3>

        <div className="space-y-4">
          <div
            className="p-4 rounded-xl"
            style={getPreviewPanelStyle(localTheme.visualStyle)}
          >
            <p className="text-sm text-slate-300 mb-3">
              Sample panel in {STYLE_OPTIONS.find((o) => o.id === localTheme.visualStyle)?.name} style
            </p>

            <div className="space-y-3">
              {localTheme.gradientEnabled && (
                <button
                  className={`px-6 py-3 rounded-xl font-medium text-white ${localTheme.buttonGlowEnabled ? 'shadow-glow' : ''}`}
                  style={{
                    background: `linear-gradient(to right, ${localTheme.gradientStartColor || localTheme.primaryColor}, ${localTheme.gradientMiddleColor || localTheme.secondaryColor}, ${localTheme.gradientEndColor || localTheme.accentColor})`,
                  }}
                >
                  Gradient Button {localTheme.buttonGlowEnabled && '✨'}
                </button>
              )}

              <button
                className={`px-6 py-3 rounded-xl font-medium text-white ${localTheme.buttonGlowEnabled ? 'shadow-glow' : ''}`}
                style={{ backgroundColor: localTheme.primaryColor }}
              >
                Primary Color Button
              </button>

              <button
                className="px-6 py-3 rounded-xl font-medium text-white"
                style={{ backgroundColor: localTheme.accentColor }}
              >
                Accent Color Button
              </button>

              {!localTheme.gradientEnabled && (
                <p className="text-xs text-slate-500 italic">Gradients disabled - toggle above to enable</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Custom CSS (Advanced)</h3>
        <p className="text-sm text-slate-400 mb-3">
          Add custom CSS to override styles. Use with caution.
        </p>

        <textarea
          value={localTheme.customCSS}
          onChange={(e) => setLocalTheme((prev) => ({ ...prev, customCSS: e.target.value }))}
          rows={6}
          placeholder=".custom-class { color: red; }"
          className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                   font-mono text-sm resize-none
                   focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                   placeholder:text-slate-600"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={handleReset}
          disabled={saving}
          className="px-6 py-3 bg-charcoal-700 hover:bg-charcoal-600 text-white font-medium rounded-xl
                   transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset to Defaults
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-royal-gradient text-white font-medium rounded-xl
                   hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed
                   shadow-glow flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Theme
            </>
          )}
        </button>
      </div>
    </div>
  );
}
